import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import { changeProductStatus, changeProductStatusBatch } from './statusesSlice';

// Типы для товара
export interface Product {
  id: string;
  name: string;
  url: string;
  thumb_url?: string;
  subject_id: number;
  subject_name: string;
  source_code?: string;
  source_title?: string;
  source_color?: string;
  status_id: string;
  status_name: string;
  status_is_positive: boolean;
  average_price: number;
  median_price: number;
  revenue: number;
  revenue_potential: number;
  revenue_average: number;
  lost_profit: number;
  country: string;
  first_date: string;
  package_width?: number;
  package_height?: number;
  package_length?: number;
  analyst_user_id: string;
  analyst_first_name: string;
  analyst_last_name: string;
  analyst_email: string;
  // Новые поля из обновленных процедур
  stat_sales?: number;
  stat_comments_count?: number;
  stat_comments_valuation?: number;
  stat_average_if_in_stock?: number;
  stat_purchase_percent?: number;
  commision_fbo?: number;
  seller_name?: string;
  article?: string;
  product_article?: string; // Поле из новых SQL функций
  order_quantity?: number | null;
  cert_id?: number | null;
  target_price_cny?: number | null;
  target_price_rub?: number | null;
  target_price_cny_ozon?: number | null;
  target_price_rub_ozon?: number | null;
  target_price_cny_wb?: number | null;
  target_price_rub_wb?: number | null;
  new_product?: boolean;
  stop_list?: boolean;
  tz_confirmed?: boolean;
  stage_1?: any;
  stage_2?: any;
}

// Типы для статистики
export interface ProductsStats {
  total_products: number;
  total_revenue: number;
  total_revenue_potential: number;
  total_lost_profit: number;
  average_price: number;
  countries: string[];
  subjects: string[];
}

// Типы для фильтров
export interface ProductSortSpec {
  field: string;
  direction: 'ASC' | 'DESC';
}

export interface ProductsFilters {
  search: string;
  subject_id: string;
  country: string;
  min_price: string;
  max_price: string;
  min_revenue: string;
  max_revenue: string;
  status_id: string;
  exclude_phrase: string;
  sort_field: string;
  /** Single ASC|DESC or comma-separated for multi-sort API */
  sort_direction: string;
  sorts: ProductSortSpec[];
  exclude_stop_list: boolean;
  exclude_confirmed: boolean;
  source_code: string;
}

// Типы для опций фильтров
export interface FilterOptions {
  subjects: Array<{ id: number; name: string }>;
  countries: string[];
  sources?: Array<{ code: string; title: string; color?: string | null }>;
}

// Типы для состояния товаров
interface ProductsState {
  products: Product[];
  stats: ProductsStats | null;
  filterOptions: FilterOptions | null;
  loading: boolean;
  error: string | null;
  filters: ProductsFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  /** Latest fetchProducts / fetchNewProducts requestId; stale responses ignored */
  listRequestId: string | null;
}

// Асинхронные действия
const fetchFilterOptions = createAsyncThunk(
  'products/fetchFilterOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getProductsFilterOptions();
      // API возвращает { success: true, data: { subjects: [...], countries: [...] } }
      // response.data содержит { success: true, data: {...} }
      // Нужно вернуть data из ответа
      const result = response.data.data || response.data;
      console.log('fetchFilterOptions result:', result);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch filter options');
    }
  }
);

const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const { page, limit } = state.products.pagination;
      const filters = state.products.filters;

      // Преобразуем фильтры для API
      const apiFilters: any = {};
      if (filters.search) apiFilters.search = filters.search;
      if (filters.subject_id) apiFilters.subject_id = filters.subject_id;
      if (filters.country) apiFilters.country = filters.country;
      if (filters.min_price) apiFilters.min_price = filters.min_price;
      if (filters.max_price) apiFilters.max_price = filters.max_price;
      if (filters.min_revenue) apiFilters.min_revenue = filters.min_revenue;
      if (filters.max_revenue) apiFilters.max_revenue = filters.max_revenue;
      if (filters.status_id) apiFilters.status_id = filters.status_id;
      if (filters.exclude_phrase) apiFilters.exclude_phrase = filters.exclude_phrase;
      const sorts = Array.isArray(filters.sorts) && filters.sorts.length > 0
        ? filters.sorts
        : (filters.sort_field
            ? [{ field: filters.sort_field, direction: filters.sort_direction || 'ASC' }]
            : []);
      if (sorts.length > 0) {
        apiFilters.sort_field = sorts.map((s: { field: string }) => s.field).join(',');
        apiFilters.sort_direction = sorts.map((s: { direction: string }) => s.direction).join(',');
      }
      // Добавляем параметры exclude_stop_list и exclude_confirmed
      // В Products.tsx по умолчанию показываем все товары (false), но можно изменить через фильтры
      if (filters.exclude_stop_list !== undefined) {
        apiFilters.exclude_stop_list = filters.exclude_stop_list;
      }
      if (filters.exclude_confirmed !== undefined) {
        apiFilters.exclude_confirmed = filters.exclude_confirmed;
      }
      if (filters.source_code) apiFilters.source_code = filters.source_code;

      console.log('fetchProducts - All filters from state:', filters);
      console.log('fetchProducts - apiFilters to send:', apiFilters);

      const response = await api.getProducts(page, limit, apiFilters);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch products');
    }
  }
);

// Асинхронное действие для загрузки товаров с фиксированным статусом "Расчет ЮЭ"
const fetchNewProducts = createAsyncThunk(
  'products/fetchNewProducts',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const { page, limit } = state.products.pagination;
      const filters = state.products.filters;

      // Преобразуем фильтры для API, но всегда добавляем фиксированный статус "Расчет ЮЭ"
      const apiFilters: any = {
        status_id: '4b8e80b8-28d9-4bc8-9fe8-262dcfe5814d', // Фиксированный статус "Расчет ЮЭ"
        // Очередь без колоночной сортировки: ниша, затем артикул. Не берём sorts из «Товаров».
        sort_field: 'subject_name,product_article',
        sort_direction: 'ASC,ASC',
      };
      if (filters.search) apiFilters.search = filters.search;
      if (filters.subject_id) apiFilters.subject_id = filters.subject_id;
      if (filters.country) apiFilters.country = filters.country;
      if (filters.exclude_phrase) apiFilters.exclude_phrase = filters.exclude_phrase;
      if (filters.min_price) apiFilters.min_price = filters.min_price;
      if (filters.max_price) apiFilters.max_price = filters.max_price;
      if (filters.min_revenue) apiFilters.min_revenue = filters.min_revenue;
      if (filters.max_revenue) apiFilters.max_revenue = filters.max_revenue;
      // Новые фильтры для стоплиста и подтвержденных товаров
      apiFilters.exclude_stop_list = filters.exclude_stop_list;
      apiFilters.exclude_confirmed = filters.exclude_confirmed;
      if (filters.source_code) apiFilters.source_code = filters.source_code;

      const response = await api.getProducts(page, limit, apiFilters);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch new products');
    }
  }
);

export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (productId: string, { rejectWithValue }) => {
    try {
      const response = await api.getProductById(productId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch product');
    }
  }
);

export const fetchProductsStats = createAsyncThunk(
  'products/fetchProductsStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.getProductsStats();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch products stats');
    }
  }
);

const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ productId, data }: { productId: string; data: {
    order_quantity?: string | number | null;
    cert_id?: string | number | null;
    required_price?: number | null;
    package_length?: number | null;
    package_width?: number | null;
    package_height?: number | null;
    article?: string | null;
    name?: string | null;
    purchase_percent?: number | null;
  } }, { rejectWithValue }) => {
    try {
      const response = await api.updateProduct(productId, data);
      return { productId, data, response: response.data };
    } catch (error: any) {
      const serverMsg = error.response?.data?.error ?? error.response?.data?.message;
      return rejectWithValue(
        (typeof serverMsg === 'string' && serverMsg) || error.message || 'Failed to update product'
      );
    }
  }
);

const confirmNewProduct = createAsyncThunk(
  'products/confirmNewProduct',
  async (
    payload: {
      productId: string;
      confirmed: boolean;
      targetPriceCny?: number | null;
      targetPriceRub?: number | null;
      targetPriceCnyOzon?: number | null;
      targetPriceRubOzon?: number | null;
      targetPriceCnyWb?: number | null;
      targetPriceRubWb?: number | null;
    },
    { rejectWithValue }
  ) => {
    try {
      const { productId, confirmed, targetPriceCny, targetPriceRub, targetPriceCnyOzon, targetPriceRubOzon, targetPriceCnyWb, targetPriceRubWb } = payload;
      const apiPayload =
        targetPriceCny !== undefined || targetPriceRub !== undefined || targetPriceCnyOzon !== undefined || targetPriceRubOzon !== undefined || targetPriceCnyWb !== undefined || targetPriceRubWb !== undefined
          ? {
              target_price_cny: targetPriceCny,
              target_price_rub: targetPriceRub,
              target_price_cny_ozon: targetPriceCnyOzon,
              target_price_rub_ozon: targetPriceRubOzon,
              target_price_cny_wb: targetPriceCnyWb,
              target_price_rub_wb: targetPriceRubWb,
            }
          : undefined;
      const response = await api.confirmNewProduct(productId, confirmed, apiPayload);
      return { productId, confirmed, targetPriceCny, targetPriceRub, targetPriceCnyOzon, targetPriceRubOzon, targetPriceCnyWb, targetPriceRubWb, response: response.data };
    } catch (error: any) {
      if (error.response?.data?.validationErrors) {
        return rejectWithValue({ message: error.message || 'Validation failed', validationErrors: error.response.data.validationErrors });
      }
      return rejectWithValue(error.message || 'Failed to confirm new product');
    }
  }
);

const addToStopList = createAsyncThunk(
  'products/addToStopList',
  async (productId: string, { rejectWithValue }) => {
    try {
      const response = await api.addToStopList(productId);
      return { productId, response: response.data };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add product to stop list');
    }
  }
);

// Начальное состояние
const initialState: ProductsState = {
  products: [],
  stats: null,
  filterOptions: null,
  loading: false,
  error: null,
  filters: {
    search: '',
    subject_id: '',
    country: '',
    min_price: '',
    max_price: '',
    min_revenue: '',
    max_revenue: '',
    status_id: '',
    exclude_phrase: '',
    sort_field: 'subject_name',
    sort_direction: 'ASC',
    sorts: [{ field: 'subject_name', direction: 'ASC' }],
    exclude_stop_list: false, // В Products.tsx по умолчанию показываем все товары
    exclude_confirmed: false, // В Products.tsx по умолчанию показываем все товары
    source_code: '',
  },
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  },
  listRequestId: null,
};

// Слайс товаров
const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Сброс страницы при изменении фильтров
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch products
      .addCase(fetchProducts.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        state.products = action.payload.data || [];
        state.pagination.total = action.payload.total || 0;
        state.pagination.totalPages = action.payload.totalPages || 0;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch filter options
      .addCase(fetchFilterOptions.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        // action.payload должен содержать { subjects: [...], countries: [...] }
        console.log('fetchFilterOptions.fulfilled payload:', action.payload);
        state.filterOptions = action.payload;
        console.log('filterOptions saved to state:', state.filterOptions);
      })
      .addCase(fetchFilterOptions.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Fetch product by ID
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false;
        // Обновляем товар в списке или добавляем новый
        const productIndex = state.products.findIndex(p => p.id === action.payload.data.id);
        if (productIndex !== -1) {
          state.products[productIndex] = action.payload.data;
        } else {
          state.products.push(action.payload.data);
        }
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch new products (with fixed status "Расчет ЮЭ")
      .addCase(fetchNewProducts.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(fetchNewProducts.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        state.products = action.payload.data || [];
        state.pagination.total = action.payload.total || 0;
        state.pagination.totalPages = action.payload.totalPages || 0;
      })
      .addCase(fetchNewProducts.rejected, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch products stats
      .addCase(fetchProductsStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductsStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.data;
      })
      .addCase(fetchProductsStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Обработка изменения статуса товара
      .addCase(changeProductStatus.fulfilled, (state, action) => {
        const { productId, statusId, statusName, statusIsPositive, generatedArticle } = action.payload;
        const product = state.products.find(p => p.id === productId);
        if (product) {
          product.status_id = statusId;
          // ВАЖНО: Обновляем название и цвет статуса сразу из payload
          // Это позволяет getCurrentStatusColor(product) в Products.tsx сразу получить правильный цвет
          // для Form.Select (className={getCurrentStatusColor(product)})
          if (statusName !== null && statusName !== undefined) {
            product.status_name = statusName;
          }
          if (statusIsPositive !== undefined) {
            product.status_is_positive = statusIsPositive;
          }
          // Если был сгенерирован артикул, обновляем его
          if (generatedArticle) {
            product.article = generatedArticle;
          }
        }
      })
      // Обработка батч-изменения статусов товаров
      .addCase(changeProductStatusBatch.fulfilled, (state, action) => {
        const { updates } = action.payload;
        // Обновляем все успешно измененные товары
        updates.forEach((update: any) => {
          const product = state.products.find(p => p.id === update.productId);
          if (product) {
            product.status_id = update.statusId;
            if (update.statusName !== null && update.statusName !== undefined) {
              product.status_name = update.statusName;
            }
            if (update.statusIsPositive !== undefined) {
              product.status_is_positive = update.statusIsPositive;
            }
            // Если был сгенерирован артикул, обновляем его
            if (update.generatedArticle) {
              product.article = update.generatedArticle;
            }
          }
        });
      })
      // Обработка обновления данных товара
      .addCase(updateProduct.fulfilled, (state, action) => {
        const { productId, data } = action.payload;
        const product = state.products.find(p => p.id === productId);
        if (product) {
          // Обновляем поля товара в локальном состоянии
          if (data.order_quantity !== undefined) {
            product.order_quantity = data.order_quantity === '' || data.order_quantity === null ? null : Number(data.order_quantity);
          }
          if (data.cert_id !== undefined) {
            product.cert_id = data.cert_id === '' || data.cert_id === null ? null : Number(data.cert_id);
          }
          if (data.article !== undefined) {
            product.article = data.article === '' || data.article === null ? undefined : data.article;
          }
          if (data.name !== undefined) {
            product.name = data.name === '' || data.name === null ? '' : data.name;
          }
          if (data.purchase_percent !== undefined) {
            const pct = data.purchase_percent === null ? undefined : Number(data.purchase_percent);
            product.stat_purchase_percent = pct != null && Number.isFinite(pct) ? pct : undefined;
          }
          // required_price обновляется в product_prices, не в products
          // package dimensions обновляются в product_metadata, не в products
        }
      })
      // Обработка добавления/удаления из стоплиста
      .addCase(addToStopList.fulfilled, (state, action) => {
        const { productId, response } = action.payload;
        const product = state.products.find(p => p.id === productId);
        if (product && response?.stop_list !== undefined) {
          product.stop_list = response.stop_list;
        }
      })
      // Обработка подтверждения нового товара
      .addCase(confirmNewProduct.fulfilled, (state, action) => {
        const { productId, confirmed, targetPriceCny, targetPriceRub, targetPriceCnyOzon, targetPriceRubOzon, targetPriceCnyWb, targetPriceRubWb } = action.payload;
        const product = state.products.find(p => p.id === productId);
        if (product) {
          product.new_product = confirmed;
          if (confirmed) {
            if (targetPriceCny !== undefined) product.target_price_cny = targetPriceCny;
            if (targetPriceRub !== undefined) product.target_price_rub = targetPriceRub;
            if (targetPriceCnyOzon !== undefined) product.target_price_cny_ozon = targetPriceCnyOzon;
            if (targetPriceRubOzon !== undefined) product.target_price_rub_ozon = targetPriceRubOzon;
            if (targetPriceCnyWb !== undefined) product.target_price_cny_wb = targetPriceCnyWb;
            if (targetPriceRubWb !== undefined) product.target_price_rub_wb = targetPriceRubWb;
          }
        }
      });
  },
});

export const { setFilters, clearFilters, setPagination, clearError, setLoading } = productsSlice.actions;
export { fetchFilterOptions, fetchNewProducts, fetchProducts, updateProduct, confirmNewProduct, addToStopList };
export default productsSlice.reducer;
